import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { AnimatePresence } from 'framer-motion';
import { motion } from '../../lib/motion';
import { useNotificationStore, GroupedNotification } from '../../store/notification.store';
import { websocketService } from '../../services/websocket.service';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

export const NotificationBell: React.FC = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const {
        unreadCount,
        isLoading,
        hasMore,
        getGroupedNotifications,
        fetchUntilGroupedTarget,
        fetchNotifications,
        markAsRead,
        markGroupAsRead, // Use the new action
        markAllAsRead,
        fetchUnreadCount,
    } = useNotificationStore();

    // Get grouped notifications from store
    const groupedNotifications = getGroupedNotifications();

    // Connect WebSocket and fetch unread count on mount
    useEffect(() => {
        // Connect to WebSocket for real-time notifications
        websocketService.connect();

        // Fetch initial unread count
        fetchUnreadCount();

        // Cleanup on unmount
        return () => {
            websocketService.disconnect();
        };
    }, [fetchUnreadCount]);

    // Smart fetch when dropdown opens - loads until we have enough grouped items
    useEffect(() => {
        if (isOpen && groupedNotifications.length === 0) {
            fetchUntilGroupedTarget(10);
        }
    }, [isOpen, groupedNotifications.length, fetchUntilGroupedTarget]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNotificationClick = async (notification: GroupedNotification) => {
        if (!notification.read) {
            if (notification.isGroup && notification.allIds && notification.allIds.length > 0) {
                // If it's a group, mark all notifications in the group as read
                await markGroupAsRead(notification.allIds);
            } else {
                // Otherwise just mark the single notification
                await markAsRead(notification.id);
            }
        }
        setIsOpen(false);
        if (notification.actionUrl) {
            navigate(notification.actionUrl);
        }
    };

    const getNotificationIcon = (type: string) => {
        switch (type) {
            case 'NEW_MESSAGE':
                return '💬';
            case 'QUOTE_RECEIVED':
                return '📝';
            case 'QUOTE_ACCEPTED':
                return '✅';
            case 'QUOTE_REJECTED':
                return '❌';
            case 'NEW_JOB_MATCH':
                return '🔔';
            case 'JOB_STARTED':
                return '🚀';
            case 'JOB_COMPLETED':
                return '🎉';
            case 'JOB_CANCELLED':
                return '🚫';
            default:
                return '📣';
        }
    };

    const formatTime = (dateString: string) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            // Check if date is valid
            if (isNaN(date.getTime())) return '';
            // Check if date is 1970 (Epoch) which usually means null backend value
            if (date.getFullYear() === 1970) return '';

            return formatDistanceToNow(date, { addSuffix: true, locale: fr });
        } catch {
            return '';
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell Icon Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 text-gray-600 hover:text-primary rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Notifications"
            >
                <Bell className="w-6 h-6" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold min-w-[20px] h-5 rounded-full flex items-center justify-center px-1">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-50"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                            <h3 className="font-semibold text-gray-900">{t('notification.title')}</h3>
                            {unreadCount > 0 && (
                                <button
                                    onClick={() => markAllAsRead()}
                                    className="text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1"
                                >
                                    <CheckCheck className="w-4 h-4" />
                                    {t('notification.markAllRead')}
                                </button>
                            )}
                        </div>

                        {/* Notification List */}
                        <div className="max-h-[400px] overflow-y-auto">
                            {groupedNotifications.length === 0 && !isLoading ? (
                                <div className="py-12 text-center text-gray-500">
                                    <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                    <p>{t('notification.empty')}</p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-100">
                                    {groupedNotifications.map((notification) => (
                                        <button
                                            key={notification.id}
                                            onClick={() => handleNotificationClick(notification)}
                                            className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex gap-3 ${!notification.read ? 'bg-primary/5' : ''
                                                }`}
                                        >
                                            {/* Icon with count badge for grouped messages */}
                                            <div className="relative flex-shrink-0">
                                                <span className="text-xl">
                                                    {getNotificationIcon(notification.type)}
                                                </span>
                                                {notification.isGroup && notification.count > 1 && !notification.read && (
                                                    <span className="absolute -top-1 -right-2 bg-primary text-white text-[10px] font-bold min-w-[16px] h-4 rounded-full flex items-center justify-center px-1">
                                                        {notification.count}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                <p className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                                    {notification.isGroup && notification.count > 1 && !notification.read
                                                        ? t('notification.grouped_messages', { count: notification.count })
                                                        : t(notification.title, notification.payload)}
                                                </p>
                                                <p className="text-sm text-gray-500 truncate">
                                                    {notification.isGroup && notification.count > 1 && !notification.read
                                                        ? t(notification.title, notification.payload)
                                                        : t(notification.message, notification.payload)}
                                                </p>
                                                <p className="text-xs text-gray-400 mt-1">
                                                    {formatTime(notification.createdAt)}
                                                </p>
                                            </div>

                                            {/* Unread indicator */}
                                            {!notification.read && (
                                                <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-2" />
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Loading */}
                            {isLoading && (
                                <div className="py-4 text-center text-gray-500">
                                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                                </div>
                            )}

                            {/* Load More */}
                            {hasMore && !isLoading && groupedNotifications.length > 0 && (
                                <button
                                    onClick={() => fetchUntilGroupedTarget(groupedNotifications.length + 10)}
                                    className="w-full py-3 text-sm text-primary hover:bg-gray-50 font-medium"
                                >
                                    {t('notification.loadMore')}
                                </button>
                            )}
                        </div>

                        {/* Footer */}
                        {groupedNotifications.length > 0 && (
                            <div className="border-t border-gray-100 px-4 py-2 bg-gray-50">
                                <Link
                                    to="/notifications"
                                    onClick={() => setIsOpen(false)}
                                    className="block text-center text-sm text-primary hover:text-primary/80 font-medium py-1"
                                >
                                    {t('notification.viewAll')}
                                </Link>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
